import * as z from 'zod'

const telemetryEnvSchema = z.object({
  VITE_TUNA_LOCAL: z.string().optional(),
  VITE_PUBLIC_POSTHOG_KEY: z.string().optional(),
  VITE_PUBLIC_POSTHOG_HOST: z.string().optional(),
  VITE_PUBLIC_POSTHOG_UI_HOST: z.string().optional(),
  VITE_SENTRY_DSN: z.string().optional(),
})

/**
 * GH #2334: a local build (`yarn kernel:app`) is a production build with `VITE_TUNA_LOCAL=true`, so it carries
 * the production PostHog key and Sentry DSN from `.env.production`. It must not report to either.
 */
const isLocalBuild = (env: z.infer<typeof telemetryEnvSchema>) => env.VITE_TUNA_LOCAL === 'true'

/** PostHog's key and hosts, or undefined when PostHog must not start */
export const getPosthogConfig = (rawEnv: unknown) => {
  const env = telemetryEnvSchema.parse(rawEnv)
  if (isLocalBuild(env) || !env.VITE_PUBLIC_POSTHOG_KEY) {
    return undefined
  }
  return {
    apiKey: env.VITE_PUBLIC_POSTHOG_KEY,
    apiHost: env.VITE_PUBLIC_POSTHOG_HOST,
    uiHost: env.VITE_PUBLIC_POSTHOG_UI_HOST,
  }
}

/**
 * GH #2084: a browser driven by automation (Playwright sets `navigator.webdriver`) is an E2E run, not a user.
 * Its Sentry envelopes were 15.3 % of every API request E2E made, counted against the WAF's rate limit, and
 * reported test noise as real errors. Real users' browsers report `false`.
 */
export const isAutomatedBrowser = (nav: { webdriver?: boolean } | undefined) => nav?.webdriver === true

/** Sentry's DSN, or undefined when Sentry must not start */
export const getSentryDsn = (rawEnv: unknown) => {
  const env = telemetryEnvSchema.parse(rawEnv)
  return isLocalBuild(env) ? undefined : env.VITE_SENTRY_DSN
}
